import React, { useState } from "react";
import { MdClose, MdDownload, MdCancel } from "react-icons/md";
import { DownloadFormat, DateFormat, DownloadOptions } from "../utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";

interface DownloadFormatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: DownloadOptions) => void;
  title: string;
  subtitle?: string;
  isBulk?: boolean;
}

const DownloadFormatModal: React.FC<DownloadFormatModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  isBulk = false,
}) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const [selectedFormat, setSelectedFormat] = useState<DownloadFormat>({
    type: "csv",
    label: "CSV",
    description: "Comma-separated values for Excel and spreadsheet applications",
  });

  const [selectedDateFormat, setSelectedDateFormat] = useState<DateFormat>({
    type: "ddmmyyyy",
    label: "DD/MM/YYYY",
    description: "Day/Month/Year format (e.g., 15/03/2024)",
  });

  const formats: DownloadFormat[] = [
    {
      type: "csv",
      label: "CSV",
      description: "Comma-separated values for Excel and spreadsheet applications",
    },
    {
      type: "json",
      label: "JSON",
      description: "JavaScript Object Notation for data processing and APIs",
    },
  ];

  const dateFormats: DateFormat[] = [
    {
      type: "ddmmyyyy",
      label: "DD/MM/YYYY",
      description: "Day/Month/Year format (e.g., 15/03/2024)",
    },
    {
      type: "mmddyyyy",
      label: "MM/DD/YYYY",
      description: "Month/Day/Year format (e.g., 03/15/2024)",
    },
    {
      type: "yyyymmdd",
      label: "YYYY-MM-DD",
      description: "ISO-like format (e.g., 2024-03-15)",
    },
    {
      type: "iso",
      label: "ISO 8601",
      description: "Full ISO format with time (e.g., 2024-03-15T10:30:00Z)",
    },
  ];

  const handleConfirm = () => {
    onConfirm({
      format: selectedFormat,
      dateFormat: selectedDateFormat,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999]" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MdDownload className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <MdClose className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {language?.groupSubscription?.downloadFormat}
              </label>
              <div className="space-y-3">
                {formats.map((format) => (
                  <label
                    key={format.type}
                    className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="radio"
                      name="format"
                      value={format.type}
                      checked={selectedFormat.type === format.type}
                      onChange={() => setSelectedFormat(format)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {format.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {format.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Date Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {language?.groupSubscription?.dateFormat}
              </label>
              <div className="space-y-2">
                {dateFormats.map((dateFormat) => (
                  <label
                    key={dateFormat.type}
                    className="flex items-start space-x-3 p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="radio"
                      name="dateFormat"
                      value={dateFormat.type}
                      checked={selectedDateFormat.type === dateFormat.type}
                      onChange={() => setSelectedDateFormat(dateFormat)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {dateFormat.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {dateFormat.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <MdCancel className="w-4 h-4 mr-2" />
              {language?.groupSubscription?.cancel}
            </button>
            <button
              onClick={handleConfirm}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <MdDownload className="w-4 h-4 mr-2" />
              {language?.groupSubscription?.download}{" "}
              {isBulk
                ? language?.groupSubscription?.all
                : language?.groupSubscription?.invoice}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DownloadFormatModal;
